import * as maptalks from "maptalks"
import { BaseObject } from "maptalks.three"
import { MeshBasicMaterial, ShaderMaterial, Color } from "three"

const OPTIONS = {
  altitude: 0,
}

const DEFAULT_LINE_OPTION = {
  color: "#000",
  opacity: 1,
}
const DEFAULT_LINE_EFFECT_OPTION = {
  color: "#000",
  opacity: 1,
}

// In case we want to open/close this animation per project.
const ENABLE_ANIMATED_PATH =
  process.env.REACT_APP_FEATURE_FLAG_ANIMATED_PATH === "true"

export class NavigationPath extends BaseObject {
  constructor(
    feature,
    layer,
    properties,
    options,
    lineOptions = DEFAULT_LINE_OPTION,
    outlineOption = DEFAULT_LINE_EFFECT_OPTION
  ) {
    options = maptalks.Util.extend({}, OPTIONS, options, {
      layer,
    })
    super()
    //Initialize internal configuration
    this._initOptions(options)
    const { altitude = OPTIONS.altitude } = options
    this.properties = { ...properties }
    this._createGroup()

    const { color: lineColor, opacity: lineOpacity } =
      lineOptions || DEFAULT_LINE_OPTION

    const staticMaterial = new MeshBasicMaterial({
      transparent: true,
      color: lineColor || "#fff",
      opacity: lineOpacity || 1,
      depthWrite: false,
    })

    const uniforms = {
      time: { value: 0 },
      color: { value: new Color(lineColor || "#fff") },
      opacity: { value: lineOpacity || 1 },
    }
    this._uniforms = uniforms
    this._t = 0 // Start time = 0

    /**
     * Animation formula
     * float dash = smoothstep(0.3, 0.7, abs(sin(vUv.x * 1.0 - time * 0.1)));
     *  |  vUv.x * 1.0 |  controls dash length & frequency
     *  | - time * 1.0 |  animates motion to the right
     */
    const animatedMaterial = new ShaderMaterial({
      uniforms,
      transparent: true,
      depthWrite: false,
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float time;
        uniform vec3 color;
        uniform float opacity;
        varying vec2 vUv;

        void main() {
          float dash = smoothstep(0.3, 0.7, abs(sin(vUv.x * 1.0 - time * 0.1)));
          gl_FragColor = vec4(color, opacity * dash);
        }
      `,
    })

    const pathGeometry = maptalks.GeoJSON.toGeometry(feature)
    const line = layer.toPath(
      pathGeometry,
      {
        interactive: false,
        cornerRadius: 2,
        width: 0.5,
      },
      ENABLE_ANIMATED_PATH ? animatedMaterial : staticMaterial
    )

    const { color: outlineColor, opacity: outlineOpacity } = outlineOption || {}
    const outlineMaterial = new MeshBasicMaterial({
      transparent: true,
      color: outlineColor || "#fff",
      opacity: outlineOpacity || 1,
    })

    const lineOutLine = layer.toPath(
      pathGeometry,
      {
        interactive: false,
        cornerRadius: 2,
        width: 1,
      },
      outlineMaterial
    )
    this.getObject3d().add(lineOutLine.getObject3d())
    this.getObject3d().add(line.getObject3d())

    // set object3d position
    const z = layer.altitudeToVector3(altitude + 0.25, altitude + 0.25).x
    const pos = this.getObject3d().position

    const position = layer.coordinateToVector3([pos.x, pos.y], z)
    // _.set(this.properties, "default.position", position)

    this.getObject3d().position.copy(position)
  }

  _animation() {
    this._t = this._t + 1
    this._uniforms.time.value = this._t
  }

  // remove() only detaches this path from the scene graph — it leaves the
  // line/outline geometries and materials (including the compiled shader
  // program) resident in GPU memory. Every route drawn/cleared over a long
  // kiosk session leaks this way, so callers must dispose before removing.
  dispose() {
    this.getObject3d()?.traverse((child: any) => {
      child.geometry?.dispose?.()
      const material = child.material
      if (Array.isArray(material)) {
        material.forEach((m) => m?.dispose?.())
      } else {
        material?.dispose?.()
      }
    })
  }
}
